/**
 * 领域词汇 —— 见 GLOSSARY.md。
 *
 * 这一层是「产品的语言」，不含存储与实现细节。`ScopedStore` 的 interface
 * 签名（packages/server/src/store/types.ts）用这里的类型，因为它对外表达的是
 * 领域概念，而不是数据库行。
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// 可见性
// ---------------------------------------------------------------------------

/** 两档可见性。整个产品只有这一个开关。 */
export const VISIBILITIES = ["shared", "private"] as const;
export type Visibility = (typeof VISIBILITIES)[number];

// ---------------------------------------------------------------------------
// 身份
// ---------------------------------------------------------------------------

/**
 * 越权测试矩阵的身份轴（spec §4.5）。
 *
 * 这五个身份是**测试与 seed 的词汇**，不是业务枚举 —— 生产代码里不存在
 * 「我现在是 U5」这种判断。
 */
export const IDENTITIES = ["U1", "U2", "U3", "U4", "U5"] as const;
export type Identity = (typeof IDENTITIES)[number];

export const IDENTITY_LABELS: Record<Identity, string> = {
  U1: "本人",
  U2: "配对对象",
  U3: "已登录未配对",
  U4: "匿名",
  U5: "另一对情侣的用户",
};

// ---------------------------------------------------------------------------
// 用户名
// ---------------------------------------------------------------------------

/**
 * 用户名 1–20 字符，允许中文与 emoji（spec §5.1.4）。
 *
 * 刻意允许 emoji：这是自用的双人工具，拦 emoji 只会让用户自己想不出用户名。
 */
export const usernameSchema = z
  .string()
  .min(1, "用户名不能为空")
  .max(20, "用户名最多 20 个字符");

/**
 * 密码最小 8 位，**不强制复杂度**（spec §5.1.2）。
 *
 * 威胁模型里没有撞库者，只有「伴侣猜密码」。强制复杂度对前者防御有限，
 * 对后者毫无作用 —— 它只会让用户把密码写在便签上贴显示器。
 * 真正有效的是足够长。
 */
export const passwordSchema = z.string().min(8, "密码至少 8 位");

// ---------------------------------------------------------------------------
// 初始化
// ---------------------------------------------------------------------------

export const setupRequestSchema = z.object({
  /** 两个人的用户名与密码，各一套。 */
  people: z
    .array(
      z.object({
        username: usernameSchema,
        password: passwordSchema,
      }),
    )
    .length(2, "初始化必须一次创建两个账号"),
});

export type SetupRequest = z.infer<typeof setupRequestSchema>;

export const loginRequestSchema = z.object({
  username: usernameSchema,
  password: z.string().min(1),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

// ---------------------------------------------------------------------------
// 设置与账号管理（spec §5.8 / §5.1.2–§5.1.4）
// ---------------------------------------------------------------------------

/**
 * `PATCH /api/settings/profile` 的请求体。未出现的字段保持原值。
 *
 * 至少要带一个字段 —— 空请求没有意义，放过去只会掩盖客户端的 bug。
 * 校验只在这里做「长度 1–20」这一层；唯一性由数据库唯一约束兜底（spec §5.1.4）。
 */
export const updateProfileRequestSchema = z
  .object({
    username: usernameSchema.optional(),
    default_visibility: z.enum(VISIBILITIES).optional(),
  })
  .refine(
    (body) =>
      body.username !== undefined || body.default_visibility !== undefined,
    { message: "至少要改一项" },
  );

export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

/**
 * `POST /api/settings/password` 的请求体。
 *
 * 需要当前密码 —— 见 `app.ts` 的实施期裁定：手机被伴侣拿在手里时，只凭一个
 * 未锁屏的会话就能把密码改掉、把主人锁在外面，比多输一次密码糟得多。
 * 忘了密码走运维重置（spec §5.1.3）。
 */
export const changePasswordRequestSchema = z.object({
  current_password: z.string().min(1),
  new_password: passwordSchema,
});

export type ChangePasswordRequest = z.infer<
  typeof changePasswordRequestSchema
>;

// ---------------------------------------------------------------------------
// 日记
// ---------------------------------------------------------------------------

/** 心情标签来自预置集合，不做自由输入。 */
export const MOODS = ["平静", "开心", "难过", "想 TA 了", "生气", "疲惫"] as const;
export type Mood = (typeof MOODS)[number];

export const createEntryRequestSchema = z.object({
  title: z.string().max(200),
  body: z.string().max(100_000),
  /** 预置集合之外的值不接受。 */
  mood: z.enum(MOODS).nullable().optional(),
  /**
   * 不传就落到**本人**的「新日记默认可见范围」（spec §5.8）。
   *
   * 刻意**不给 zod 默认值**：默认值会让「没想清楚」与「明确选 shared」
   * 变成同一个输入，于是用户设置的默认范围永远生效不了。
   */
  visibility: z.enum(VISIBILITIES).optional(),
  allow_reply: z.boolean().default(true),
});

export type CreateEntryRequest = z.infer<typeof createEntryRequestSchema>;

/**
 * 增量更新（草稿自动保存）—— spec §5.3 的 `ENTRY-1`。
 *
 * `expected_updated_at` **必填**，这是「乐观锁」的全部实现方式：客户端把它
 * 读到的那一版 `updated_at` 带回来，服务端只在它仍然是最新版时才写。
 * 缺了它，两个设备同时编辑时，晚到的那次写入会**安静地**抹掉早到的那次 ——
 * 没有报错、没有冲突，只有一段被覆盖的记忆。
 *
 * 字段全部可选：自动保存一次可能只动了正文，也可能只拨了一下
 * 「允许 TA 回应」。未出现的字段保持原值。
 */
export const updateEntryRequestSchema = z.object({
  title: z.string().max(200).optional(),
  body: z.string().max(100_000).optional(),
  /** 预置集合之外的值不接受；显式传 `null` 表示清空心情。 */
  mood: z.enum(MOODS).nullable().optional(),
  allow_reply: z.boolean().optional(),
  /** 客户端手上的版本。不匹配就是「这篇在别处被改过」。 */
  expected_updated_at: z.string().min(1),
});

export type UpdateEntryRequest = z.infer<typeof updateEntryRequestSchema>;

/**
 * 回应的预置短句（spec §5.4.3）。
 *
 * 放在 shared 是因为它既是界面要渲染的按钮，也是「回应只能是短句」这一产品
 * 意图的载体 —— 不是后端校验用的枚举（回应本来就可以自由输入）。
 */
export const REPLY_PRESETS = [
  "我也想看",
  "等我一周",
  "抱抱你",
  "我记住了",
  "明天跟你说",
] as const;

/**
 * 回应正文。**不可编辑**（spec §3.1），所以只有创建时校验。
 *
 * 上限 2000 是工程限，不是产品限 —— 回应是短评，不是另一篇日记。
 */
export const replyBodySchema = z.string().min(1).max(2000);

/** `POST /api/entries/:id/replies` 的请求体。 */
export const createReplyRequestSchema = z.object({
  body: replyBodySchema,
});

export type CreateReplyRequest = z.infer<typeof createReplyRequestSchema>;

/** 可见性迁移的请求体（spec §4.2）。只接受两个值，其余一律 400。 */
export const updateVisibilityRequestSchema = z.object({
  visibility: z.enum(VISIBILITIES),
});

export type UpdateVisibilityRequest = z.infer<
  typeof updateVisibilityRequestSchema
>;

// ---------------------------------------------------------------------------
// 附件（spec §5.3 / §5.6）
// ---------------------------------------------------------------------------

/** 单篇日记最多 9 张图（spec §5.3 `ENTRY-1`）。 */
export const MAX_ATTACHMENTS_PER_ENTRY = 9;

/** 单张压到 2MB 以内（spec §5.3 `ENTRY-1`）。 */
export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;

/**
 * 允许上传的图片扩展名（同时是七牛对象名的一部分）。
 *
 * `heic` 在列：iPhone 默认拍它。**2026-10-03 已用真实 HEIC 文件实测：七牛原生
 * 支持解码并转码**（`imageMogr2/format/jpg` 持久化处理返回 code 0，输入
 * `image/heic` → 输出 `image/jpeg`）。所以不需要客户端先转码，spec §8.5 的未核实项
 * 到此关闭。
 */
export const ATTACHMENT_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "heic",
] as const;
export type AttachmentExt = (typeof ATTACHMENT_EXTENSIONS)[number];

// ---------------------------------------------------------------------------
// 导出（spec §5.7 `EXPORT-1`）
// ---------------------------------------------------------------------------
//
// 导出是 v1 唯一的数据出口（票 15 裁定不做备份）。两种格式各有分工：
// Markdown 是不依赖本软件的归档，**能读不能恢复**；JSON 是唯一可用于重建的
// 结构化副本。所以 JSON 的字段必须完整，且附件存稳定标识（`qiniuKey`），
// **绝不能只存会过期的签名 URL**（spec §5.7）。

/** 导出包格式标识。将来做重建工具时据此认版本。 */
export const EXPORT_FORMAT = "wodiary-export";
/** 结构化副本的版本号。字段含义变化时递增。 */
export const EXPORT_VERSION = 1;

/**
 * 导出里的一个附件。
 *
 * ⚠️ `qiniuKey` 是**稳定标识**。签名 URL 会在几分钟后过期，把它写进导出
 * 等于制造一批死链接（而且不会报错）；key 任何时候都能重新签出 URL。
 */
export interface ExportAttachment {
  id: string;
  qiniuKey: string;
  width: number;
  height: number;
  bytes: number;
}

/** 导出里的一条回应。字段与 `ReplyView` 同形（重建需要完整字段）。 */
export interface ExportReply {
  id: string;
  authorId: string;
  authorUsername: string;
  body: string;
  createdAt: string;
}

/**
 * 导出里的一篇日记 —— **完整字段**，是重建的基本单位。
 *
 * 回应与附件嵌在里面：导出是「我视角下的全部」，所以它们天然属于某篇日记，
 * 不需要调用方自己去 join。
 */
export interface ExportEntry {
  id: string;
  title: string;
  body: string;
  mood: Mood | null;
  visibility: Visibility;
  allowReply: boolean;
  writtenAt: string;
  updatedAt: string;
  /** 回执（spec §5.4.4）：只有作者本人看得到自己的共享日记被谁读过。 */
  readAt: string | null;
  ownerId: string;
  authorUsername: string;
  attachments: ExportAttachment[];
  replies: ExportReply[];
}

/** Markdown 归档的一个文件。文件名如 `2026-10.md`（按年月分）。 */
export interface ExportMarkdownFile {
  filename: string;
  content: string;
}

/**
 * 一次导出的完整产物。
 *
 * `entries` 是 JSON 那一路（可重建），`markdown` 是 Markdown 那一路
 * （按年月分文件）。两者由同一次可见性推导生成 —— 不可能是两份不同的数据。
 */
export interface ExportBundle {
  format: typeof EXPORT_FORMAT;
  version: number;
  exportedAt: string;
  /**
   * 导出时的作用域：谁导的、配对对象是谁（未配对为 `null`）。
   *
   * `selfId` 为 `null` 只在匿名调用时出现（那个归档必然是空的）；HTTP 端点
   * 要求登录，所以真实产物里它恒非空。
   */
  scope: { selfId: string | null; partnerId: string | null };
  entries: ExportEntry[];
  markdown: ExportMarkdownFile[];
}

// ---------------------------------------------------------------------------
// 纪念日（spec §3.1 / §5.5）
// ---------------------------------------------------------------------------

/**
 * 纪念日日期：`YYYY-MM-DD`，**带年份**。
 *
 * 为什么不接受「只有月日」：`date` 带年份才能算出「在一起第 N 年」。展示时
 * 是否显示年份由类型决定（生日不显示年份），但**存储层一律存全年份** ——
 * 少了年份，那个「第 N 年」永远算不出来。
 *
 * 正则之外还校验日期的真实性：正则拦得住格式，拦不住 `2026-02-30`。
 */
export const anniversaryDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "日期格式是 YYYY-MM-DD")
  .refine((s) => {
    const [y, m, d] = s.split("-").map(Number);
    if (y === undefined || m === undefined || d === undefined) return false;
    const date = new Date(Date.UTC(y, m - 1, d));
    return (
      date.getUTCFullYear() === y &&
      date.getUTCMonth() === m - 1 &&
      date.getUTCDate() === d
    );
  }, "这不是一个真实存在的日期");

/**
 * 空状态的预填引导标题（spec §5.5）：纪念日 tab 会被高频看到，空态必须给引导
 * 而不是空白页。绝大多数人的第一条都是它。
 */
export const ANNIVERSARY_PRESET_TITLE = "在一起纪念日";

/** `POST /api/anniversaries` 的请求体。 */
export const createAnniversaryRequestSchema = z.object({
  title: z.string().min(1).max(100),
  date: anniversaryDateSchema,
  visibility: z.enum(VISIBILITIES).default("shared"),
  remind_enabled: z.boolean().default(true),
});

export type CreateAnniversaryRequest = z.infer<
  typeof createAnniversaryRequestSchema
>;

/** `PATCH /api/anniversaries/:id` 的请求体。未出现的字段保持原值。 */
export const updateAnniversaryRequestSchema = z.object({
  title: z.string().min(1).max(100).optional(),
  date: anniversaryDateSchema.optional(),
  visibility: z.enum(VISIBILITIES).optional(),
  remind_enabled: z.boolean().optional(),
});

export type UpdateAnniversaryRequest = z.infer<
  typeof updateAnniversaryRequestSchema
>;

// ---------------------------------------------------------------------------
// 配对（spec §5.2 / §6.3）
// ---------------------------------------------------------------------------

/**
 * 配对状态机。
 *
 * 三个 `pending` 子状态是必要的：同一段未激活的关系，在双方眼里是三个不同的
 * 画面（我要出示码 / 我该确认 / 我在等对方确认），而界面必须区分它们。
 *
 * `unpaired` 包含「初始化完成后还没开始配对」与「关系已解散」两种情况 ——
 * v1 不区分，因为应用内不存在解散入口（spec §5.9：只由运维命令写入）。
 */
export const PAIRING_STATES = [
  "unpaired",
  "awaiting_partner",
  "awaiting_my_confirm",
  "awaiting_partner_confirm",
  "active",
] as const;
export type PairingState = (typeof PAIRING_STATES)[number];

/**
 * 输入配对码。
 *
 * 6 位数字 —— 格式不对是 **400**，不会走到数据层。
 * 刻意用字符串而不是数字：`000123` 是一个合法的 6 位码，转成数字就没了前导零。
 */
export const acceptPairingCodeRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "配对码是 6 位数字"),
});

export type AcceptPairingCodeRequest = z.infer<
  typeof acceptPairingCodeRequestSchema
>;

// ---------------------------------------------------------------------------
// 领域错误
// ---------------------------------------------------------------------------

/**
 * 这里**刻意没有** `NOT_FOUND` / `NotFound` 哨兵值。
 *
 * 「拿不到」在 interface 层就是 `null`（见 `packages/server/src/store/types.ts`），
 * 由 HTTP 层负责映射成 404。曾经这里放过一个 `Symbol("not-found")`：没有任何
 * 调用方，而它自己的注释说的正好是「用 `null` 而不是哨兵」—— 两种「拿不到」
 * 的表示法并存，迟早会有人用错那个没人识别的。
 */
